import {Box, Grid, Radio, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useState} from 'react';
import AurButton from '../../../common/aur-button/AurButton';
import {getRadioIcons} from './FieldView';
import {isEmpty} from '../../../../utils/utils';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';

export interface ICustomInfo {
  title: string;
  value?: string;
  paramKey?: string;
  isEditable?: boolean;
  isEditModeDisabled?: boolean;
  optionArray?: string[];
  onSaveKey?: (paramKey: string, value?: string) => void;
  onChangeEditMode?: (paramKey: string, isEditMode: boolean) => void;
  isDate?: boolean;
}

export interface ICustomInfoProps {
  value1?: ICustomInfo;
  value2?: ICustomInfo;
  value3?: ICustomInfo;
  value4?: ICustomInfo;
  disabled?: boolean;
  editModeParamKey?: string;
  onSaveKey?: (paramKey: string, value?: string) => void;
  onChangeEditMode?: (paramKey: string, isEditMode: boolean) => void;
}

const CustomInfoEditableWithOptions: React.FC<ICustomInfo> = ({
  title,
  value,
  paramKey = '',
  isEditModeDisabled,
  optionArray,
  onChangeEditMode,
  onSaveKey,
}) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [updatedValue, setUpdatedValue] = useState<string>(value ?? '');

  useEffect(() => {
    setUpdatedValue(value ?? '');
  }, [value]);

  const onClickSave = useCallback(() => {
    setIsEditMode((prevIsEditMode) => {
      if (prevIsEditMode) {
        setUpdatedValue(value ?? '');
        onSaveKey?.(paramKey, updatedValue);
      }
      onChangeEditMode?.(paramKey, !prevIsEditMode);
      return !prevIsEditMode;
    });
  }, [onChangeEditMode, onSaveKey, paramKey, updatedValue, value]);

  const handleChangeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatedValue(event.target.value);
    },
    []
  );

  return (
    <>
      {title && value ? (
        <Box className={'item-row'}>
          <Typography className="typo-text subhead4_size title-text" tabIndex={0}>
            {title}
          </Typography>
          {isEditMode ? (
            <Box className="field-radio-group-container">
              {(optionArray ?? []).map((item) => {
                return (
                  <Box className="field-radio-item" key={item}>
                    <Radio
                      onChange={handleChangeValue}
                      checked={(updatedValue ?? '') === item}
                      name="multi-value-radio"
                      value={item}
                      inputProps={{
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        'data-testid': item,
                      }}
                      checkedIcon={
                        <img
                          alt={''}
                          src={getRadioIcons(
                            (updatedValue ?? '') === item,
                            false
                          )}
                        />
                      }
                    />
                    <Typography
                      component="span"
                      className="typo-text subhead3_size"
                      tabIndex={0}
                    >
                      {item}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography className="typo-text body2_size title-info-text" tabIndex={0}>
              {value}
            </Typography>
          )}
          <AurButton
            onClick={onClickSave}
            buttonType={'submit'}
            type={'text'}
            title={isEditMode ? t('common.save') : t('common.edit')}
            label={isEditMode ? t('common.save') : t('common.edit')}
            size={'Medium'}
            disabled={isEditModeDisabled}
            dataTestId="edit-save-metadata"
          />
        </Box>
      ) : null}
    </>
  );
};

const CustomInfo: React.FC<ICustomInfoProps> = ({
  value1,
  value2,
  value3,
  value4,
  disabled = false,
  editModeParamKey = '',
  onSaveKey,
  onChangeEditMode,
}) => {
  const getDisableTextStyle = useCallback(
    (pKey: string) =>
      !isEmpty(editModeParamKey) && pKey !== editModeParamKey && disabled
        ? 'text-disabled'
        : '',
    [editModeParamKey, disabled]
  );

  const renderItem = useCallback(
    (data: ICustomInfo | undefined) => {
      if (data?.isEditable) {
        return (
          <CustomInfoEditableWithOptions
            {...data}
            onSaveKey={onSaveKey}
            onChangeEditMode={onChangeEditMode}
          />
        );
      }

      return (
        data?.title &&
        data?.value && (
          <Box className={'item-row'}>
            <Typography
              tabIndex={0}
              className={`typo-text subhead4_size title-text ${getDisableTextStyle(data?.paramKey ?? '')}`}
            >
              {data.title}
            </Typography>
            {data.isDate ? (
              <AurLightTooltip title={t('common.yearMonthDate')}>
                <Typography
                  tabIndex={0}
                  className={`typo-text body2_size title-info-text ${getDisableTextStyle(data?.paramKey ?? '')}`}
                >
                  {data.value}
                </Typography>
              </AurLightTooltip>
            ) : (
              <Typography
                tabIndex={0}
                className={`typo-text body2_size title-info-text ${getDisableTextStyle(data?.paramKey ?? '')}`}
              >
                {data.value}
              </Typography>
            )}
          </Box>
        )
      );
    },
    [getDisableTextStyle, onChangeEditMode, onSaveKey]
  );

  return (
    <Box
      data-testid="custom-field-container"
      className={'custom-field-info-container'}
    >
      <Grid container>
        <Grid item xs={6}>
          <Box className={'item-column'}>
            {renderItem(value1)}
            {renderItem(value2)}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box className={'item-column'}>
            {renderItem(value3)}
            {renderItem(value4)}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export {CustomInfo};
